import { ICurriculumItemableSearchResult } from 'holberton-school-intranet-api';
import * as React from 'react';
import { ReactElement } from 'react';

import { pluralize } from '../../utils';
import Icon from '../common/Icon';
import CurriculumItemableCurriculumsLink from './CurriculumItemableCurriculumsLink';

interface IProps {
    item: ICurriculumItemableSearchResult;
    renderActions?: () => ReactElement;
}

function resolveProjectId(
    item: ICurriculumItemableSearchResult,
): number | undefined {
    if (item.id) {
        return Number(item.id);
    }

    const objectID = (item as { objectID?: string }).objectID;
    if (!objectID) {
        return undefined;
    }

    const match = objectID.match(/_(\d+)$/);
    return match ? Number(match[1]) : undefined;
}

export default function CurriculumItemableSummaryCard({
    item,
    renderActions,
}: IProps): ReactElement {
    const projectId = resolveProjectId(item);
    return (
        <div className="row">
            <div className="col-lg-10 col-sm-12">
                <div className="align-items-center d-flex gap-3">
                    {item.author_id && (
                        <Icon
                            name="lock"
                            title={`This project is visible only by you and your school location staff members (Authored by ${item.author_name})`}
                        />
                    )}

                    {item.online === false && (
                        <Icon
                            name="toggle-off"
                            title="This project is offline"
                        />
                    )}

                    <a
                        className="fw-bold td-none"
                        href={item.uri || item.link}
                        rel="noreferrer"
                        target="_blank"
                    >
                        {item.name || item.label}
                    </a>

                    {item.tags?.length > 0 && (
                        <div className="align-items-center d-flex gap-2">
                            {item.tags.map((tag) => (
                                <span className="label label-default" key={tag}>
                                    {tag}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                <small className="align-items-center d-flex gap-2 text-muted">
                    <span>#{item.id}</span>

                    {item.level && (
                        <>
                            <span>•</span>
                            <Icon name="level-up" text={item.level} />
                        </>
                    )}
                    {item.work_hours !== undefined && (
                        <>
                            <span>•</span>
                            <Icon
                                name="clock-o"
                                text={pluralize(item.work_hours, 'hour')}
                            />
                        </>
                    )}
                    {item.correction_type && (
                        <>
                            {<span>•</span>}
                            <Icon
                                name="check-circle"
                                text={item.correction_type}
                            />
                        </>
                    )}
                    {item.tasks_count !== undefined && (
                        <>
                            {<span>•</span>}
                            <Icon
                                name="tasks"
                                text={pluralize(item.tasks_count, 'task')}
                            />
                        </>
                    )}
                    {item.curriculums_count > 0 && projectId && (
                        <>
                            <span>•</span>
                            <CurriculumItemableCurriculumsLink
                                curriculumsCount={item.curriculums_count}
                                inline
                                projectId={projectId}
                            />
                        </>
                    )}
                </small>
            </div>

            <div className="col-lg-2 col-sm-12 text-right">
                {renderActions && renderActions()}
            </div>
        </div>
    );
}
